import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from 'sonner'
import { api } from '@/lib/api'
import {
  ArrowLeft,
  CheckCircle2,
  Server,
  ShieldCheck,
  Map,
  Play,
  RefreshCw,
  Mail,
  AlertCircle,
  BookOpen,
} from 'lucide-react'

export function ImportGuide() {
  const [testForm, setTestForm] = useState({
    source_email: '',
    imap_host: '',
    imap_port: 993,
    imap_username: '',
    imap_password: '',
    imap_use_ssl: true,
  })
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ ok: boolean; folders: { name: string; messages: number }[] } | null>(null)

  const handleTest = async () => {
    if (!testForm.imap_host || !testForm.imap_username || !testForm.imap_password) {
      toast.error('Please fill in host, username, and password')
      return
    }
    setTesting(true)
    setTestResult(null)
    try {
      const res = await api.post('/imports/test-connection', {
        source_email: testForm.source_email || testForm.imap_username,
        imap_host: testForm.imap_host,
        imap_port: Number(testForm.imap_port),
        imap_username: testForm.imap_username,
        imap_password: testForm.imap_password,
        imap_use_ssl: testForm.imap_use_ssl,
      })
      setTestResult(res.data)
      toast.success('IMAP connection successful')
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'IMAP connection failed')
    } finally {
      setTesting(false)
    }
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/migration">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Migration Guide</h1>
          <p className="text-sm text-muted-foreground">
            Move emails from any standard IMAP server into PurpleToad Mail, one mailbox at a time.
          </p>
        </div>
      </div>

      <Card className="border-blue-500/20 bg-blue-500/5">
        <CardContent className="p-4 flex gap-3 items-start">
          <AlertCircle className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium">Standard IMAP only — one mailbox per migration</p>
            <p className="text-muted-foreground">
              PurpleToad Mail imports from any RFC-compliant IMAP server using a username and password (or app password).
              To move multiple addresses (for example support@, hello@, and founder@), create one migration for each mailbox.
              OAuth-only providers such as Gmail, Yahoo, and Outlook are not supported in this release.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Server className="h-4 w-4 text-muted-foreground" />
              IMAP server details
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Ask your current provider for the IMAP host, port, and whether to use SSL/TLS or STARTTLS.
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              App password
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Use your mailbox password or an app-specific password if your provider requires it. Never reuse your PurpleToad Mail password.
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-muted-foreground" />
              Enable IMAP access
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Make sure IMAP access is enabled in your current provider's control panel before testing.
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Server className="h-4 w-4" />
            Step 1 — Verify your IMAP connection
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Source email (optional)</Label>
              <Input
                value={testForm.source_email}
                onChange={(e) => setTestForm((f) => ({ ...f, source_email: e.target.value }))}
                placeholder="you@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>IMAP Host</Label>
              <Input
                value={testForm.imap_host}
                onChange={(e) => setTestForm((f) => ({ ...f, imap_host: e.target.value }))}
                placeholder="imap.example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Port</Label>
              <Input
                type="number"
                value={testForm.imap_port}
                onChange={(e) => setTestForm((f) => ({ ...f, imap_port: Number(e.target.value) }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Connection Security</Label>
              <Select
                value={testForm.imap_use_ssl ? 'ssl' : 'starttls'}
                onValueChange={(v) =>
                  setTestForm((f) => ({
                    ...f,
                    imap_use_ssl: v === 'ssl',
                    imap_port: v === 'ssl' ? 993 : 143,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ssl">SSL / TLS (993)</SelectItem>
                  <SelectItem value="starttls">STARTTLS (143)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>IMAP Username</Label>
              <Input
                value={testForm.imap_username}
                onChange={(e) => setTestForm((f) => ({ ...f, imap_username: e.target.value }))}
                placeholder="you@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Password / App Password</Label>
              <Input
                type="password"
                value={testForm.imap_password}
                onChange={(e) => setTestForm((f) => ({ ...f, imap_password: e.target.value }))}
                placeholder="••••••••"
              />
            </div>
          </div>
          <Button
            onClick={handleTest}
            disabled={testing}
            className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
          >
            {testing ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
            Test Connection
          </Button>

          {testResult && (
            <div className="rounded-lg border p-3 space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Connection successful — {testResult.folders.length} folders found
              </p>
              <div className="flex flex-wrap gap-2">
                {testResult.folders.map((f) => (
                  <Badge key={f.name} variant="outline">
                    {f.name} ({f.messages})
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Map className="h-4 w-4" />
            Step 2 — Create one migration per mailbox
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>
            From the <Link to="/migration" className="text-violet-400 hover:underline">Migration Center</Link> page, create a new
            migration using the credentials you verified above. Choose the target domain and the exact mailbox where messages should
            land.
          </p>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li>Each migration copies exactly one source mailbox.</li>
            <li>INBOX, Sent, and Sent Items are mapped automatically.</li>
            <li>Junk and Spam are skipped by default to avoid importing unwanted mail.</li>
            <li>Drafts and Trash are skipped by default; enable the switches if you want them.</li>
            <li>Custom folders keep their original names.</li>
          </ul>
          <div className="flex gap-2 pt-2">
            <Button asChild>
              <Link to="/migration">
                <Play className="mr-2 h-4 w-4" />
                Go to Migration Center
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Step 3 — Track progress
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            Once started, the import runs in the background. The dashboard updates the percentage, current folder, and
            message counters every few seconds. You can:
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>Preview source folders and recent message headers before starting.</li>
            <li>Cancel an in-progress import at any time; already-synced messages stay in PurpleToad Mail.</li>
            <li>Resume automatically from the last UID checkpoint if the worker restarts.</li>
          </ul>
        </CardContent>
      </Card>

      <Separator />

      <Card className="border-muted">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="h-4 w-4" />
            What gets imported?
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            Imported emails are stored in PurpleToad Mail immediately and are visible in the webmail/dashboard. Attachments
            are uploaded to object storage and deduplicated by SHA-256. Messages with the same <code>Message-ID</code>{' '}
            are skipped by default to avoid duplicates.
          </p>
          <p>
            Imported history is not pushed into Dovecot in this release, so external IMAP clients connected to
            PurpleToad Mail will only see new mail, not imported history.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
